import React, { createContext, useContext, useReducer, useEffect } from "react";
import { User } from "./DataContext";

// Auth state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Auth actions
type AuthAction =
  | { type: "LOGIN_START" }
  | { type: "LOGIN_SUCCESS"; payload: User }
  | { type: "LOGIN_FAILURE"; payload: string }
  | { type: "LOGOUT" }
  | { type: "CLEAR_ERROR" }
  | { type: "UPDATE_USER"; payload: User };

// Initial auth state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "LOGIN_START":
      return { ...state, isLoading: true, error: null };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    case "UPDATE_USER":
      return { ...state, user: action.payload };
    default:
      return state;
  }
}

// Auth context
const AuthContext = createContext<{
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  clearError: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
} | null>(null);

// Sample users for demo
const sampleUsers: User[] = [
  {
    id: "1",
    name: "Admin User",
    email: "admin@syndicate.eg",
    role: "admin",
    permissions: ["all"],
    isActive: true,
    department: "Administration",
    lastLogin: new Date().toISOString(),
  },
  {
    id: "2",
    name: "Board Member",
    email: "board@syndicate.eg",
    role: "board",
    permissions: ["governance", "finance", "membership", "legal"],
    isActive: true,
    department: "Governance",
    lastLogin: new Date().toISOString(),
  },
  {
    id: "3",
    name: "Committee Chair",
    email: "committee@syndicate.eg",
    role: "committee",
    permissions: ["membership", "events", "media"],
    isActive: true,
    department: "Committees",
    committee: "International Relations",
    lastLogin: new Date().toISOString(),
  },
  {
    id: "4",
    name: "Secretariat Staff",
    email: "secretariat@syndicate.eg",
    role: "secretariat",
    permissions: ["admin", "correspondence", "calendar"],
    isActive: true,
    department: "Administration",
    lastLogin: new Date().toISOString(),
  },
  {
    id: "5",
    name: "Regular Member",
    email: "member@syndicate.eg",
    role: "member",
    permissions: ["self-service", "profile", "documents"],
    isActive: true,
    department: "Membership",
    lastLogin: new Date().toISOString(),
  },
];

// Auth provider
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing session on mount
  useEffect(() => {
    const savedUser = localStorage.getItem("syndicate-auth-user");
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        dispatch({ type: "LOGIN_SUCCESS", payload: user });
      } catch (error) {
        localStorage.removeItem("syndicate-auth-user");
      }
    }
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: "LOGIN_START" });

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Find user by email
    const user = sampleUsers.find((u) => u.email === email);

    if (!user) {
      dispatch({ type: "LOGIN_FAILURE", payload: "User not found" });
      return;
    }

    if (!user.isActive) {
      dispatch({ type: "LOGIN_FAILURE", payload: "Account is deactivated" });
      return;
    }

    // Simple password validation (in real app, this would be hashed)
    const validPasswords: { [key: string]: string } = {
      "admin@syndicate.eg": "admin123",
      "board@syndicate.eg": "board123",
      "committee@syndicate.eg": "committee123",
      "secretariat@syndicate.eg": "secretariat123",
      "member@syndicate.eg": "member123",
    };

    if (validPasswords[email] !== password) {
      dispatch({ type: "LOGIN_FAILURE", payload: "Invalid password" });
      return;
    }

    // Update last login
    const updatedUser = {
      ...user,
      lastLogin: new Date().toISOString(),
    };

    // Save to localStorage
    localStorage.setItem("syndicate-auth-user", JSON.stringify(updatedUser));

    dispatch({ type: "LOGIN_SUCCESS", payload: updatedUser });
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("syndicate-auth-user");
    dispatch({ type: "LOGOUT" });
  };

  // Update user function
  const updateUser = (user: User) => {
    localStorage.setItem("syndicate-auth-user", JSON.stringify(user));
    dispatch({ type: "UPDATE_USER", payload: user });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  // Permission check function
  const hasPermission = (permission: string): boolean => {
    if (!state.user) return false;
    if (state.user.permissions.includes("all")) return true;
    return state.user.permissions.includes(permission);
  };

  // Role check function
  const hasRole = (role: string): boolean => {
    if (!state.user) return false;
    return state.user.role === role;
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        login,
        logout,
        updateUser,
        clearError,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Auth hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
